"use client";

import { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import { User, UsersThree } from "phosphor-react";
import Modal from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { useI18N, useSyncBusinessInterest, useAuth } from "common/hooks";
import Switch from "@/components/common/Switch";
import WideButton from "@/components/common/WideButton";
import { useCustomToast } from "@/hooks/useCustomToast";
import AnalyticsService from "@/services/analytics";
import Loader from "@/components/common/Loader";
import { NotificationSuccessModal } from "./NotificationSuccessModal";
import { Bell } from "phosphor-react";

// ============================================================================
// Types
// ============================================================================

interface InterestNotificationModalProps {
  businessId: string;
  businessName: string;
  interestId?: string; // Optional: if provided, will use"update" action
  notifyAccessibilityInformation?: boolean; // Initial value for toggle
  notifyLiveAlert?: boolean; // Initial value for toggle
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onComplete?: () => void; // Called when success modal closes
  skipSuccessModal?: boolean; // If true, don't show the success modal (used when on-page success state is shown)
  interestedType?: string; // The existing interest type when updating
}

// ============================================================================
// Component
// ============================================================================

export function InterestNotificationModal({
  businessId,
  businessName,
  interestId,
  notifyAccessibilityInformation = false,
  notifyLiveAlert = false,
  isOpen,
  onClose,
  onSuccess,
  onComplete,
  skipSuccessModal = false,
  interestedType,
}: InterestNotificationModalProps) {
  const i18n = useI18N();
  const { user } = useAuth();
  const showToast = useCustomToast();

  // Interest type options
  const interestTypes = [
    { name: i18n.get("forMyself"), value: "for_myself" },
    { name: i18n.get("forSomeoneIKnow"), value: "for_someone_i_know" }
  ];

  // Initialize interest type based on existing interest or user's accessibility_need
  const getInitialInterestType = (): "for_myself" | "for_someone_i_know" | null => {
    // If updating an existing interest, use the existing interest type
    if (interestId && interestedType) {
      if (interestedType === "for_myself" || interestedType === "for_someone_i_know") {
        return interestedType;
      }
    }
    
    // Otherwise, use user's accessibility_need preference
    const accessibilityNeed = user?.accessibility_need;
    if (accessibilityNeed === "Yes, I do") {
      return "for_myself";
    }
    if (accessibilityNeed === "Yes, I know someone who does") {
      return "for_someone_i_know";
    }
    return null; // No valid match, user must select
  };

  const [selectedInterest, setSelectedInterest] = useState<"for_myself" | "for_someone_i_know" | null>(getInitialInterestType());
  const [newAccessibilityInformation, setNewAccessibilityInformation] =
    useState(false);
  const [liveAccessibilityAlerts, setLiveAccessibilityAlerts] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const {
    data: mutateResult,
    error,
    isLoading,
    mutate,
  } = useSyncBusinessInterest();

  // Track if we've already processed this success to prevent loops
  const processedResultRef = useRef<typeof mutateResult>(null);
  
  // Ref for initial focus on modal open
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Initialize state from props when modal opens
  useEffect(() => {
    if (isOpen) {
      setNewAccessibilityInformation(notifyAccessibilityInformation);
      setLiveAccessibilityAlerts(notifyLiveAlert);
      
      // Reset interest type when modal opens
      // Priority: existing interest type > user's accessibility_need preference
      if (interestId && interestedType) {
        // If updating an existing interest, use the existing interest type
        if (interestedType === "for_myself" || interestedType === "for_someone_i_know") {
          setSelectedInterest(interestedType);
        } else {
          setSelectedInterest(null);
        }
      } else {
        // Otherwise, use user's accessibility_need preference
        const accessibilityNeed = user?.accessibility_need;
        if (accessibilityNeed === "Yes, I do") {
          setSelectedInterest("for_myself");
        } else if (accessibilityNeed === "Yes, I know someone who does") {
          setSelectedInterest("for_someone_i_know");
        } else {
          setSelectedInterest(null); // Force user to select if not set
        }
      }
      
      // Set initial focus on close button after modal opens
      setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 100);
    }
  }, [isOpen, notifyAccessibilityInformation, notifyLiveAlert, interestId, interestedType, user?.accessibility_need]);

  // Handle error
  useEffect(() => {
    if (error) {
      showToast(error.message, "error");
    }
  }, [error, showToast]);

  // Handle success
  useEffect(() => {
    if (mutateResult && processedResultRef.current !== mutateResult) {
      processedResultRef.current = mutateResult;

      if (mutateResult.status === "success") {
        // Track successful submission
        AnalyticsService.trackGAEventHandler("submittedUserInterest");
        AnalyticsService.trackMx("submittedUserInterest", {
          BusinessId: businessId,
          BusinessName: businessName,
          InterestType: selectedInterest,
          NotifyAccessibilityInformation: newAccessibilityInformation,
          NotifyLiveAlert: liveAccessibilityAlerts,
          ReviewerName: user?.name,
        });

        // Close the interest modal
        onClose();

        // Check if at least one notification option is enabled
        const hasAtLeastOneNotification =
          newAccessibilityInformation || liveAccessibilityAlerts;

        if (skipSuccessModal) {
          // Don't show success modal, just call onComplete directly
          // (used when on-page success state will be shown instead)
          if (onComplete) {
            onComplete();
          }
          // Show toast for feedback
          showToast(mutateResult.message, "success");
        } else if (hasAtLeastOneNotification) {
          // Show the success modal if at least one notification is enabled
          setShowSuccessModal(true);
        } else {
          // Show toast if both notifications are disabled
          showToast(mutateResult.message, "success");
        }

        if (onSuccess) {
          onSuccess();
        }
      } else {
        showToast(mutateResult.message, "error");
      }
    }
    // We intentionally exclude newAccessibilityInformation and liveAccessibilityAlerts from deps
    // to prevent infinite loops, as they're only used for analytics tracking
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mutateResult,
    businessId,
    businessName,
    selectedInterest,
    user?.name,
    onClose,
    onSuccess,
    onComplete,
    skipSuccessModal,
    showToast,
  ]);

  const handleClose = () => {
    // Track cancellation
    AnalyticsService.trackGAEventHandler("cancelledUserInterest");
    AnalyticsService.trackMx("cancelledUserInterest", {
      BusinessId: businessId,
      BusinessName: businessName,
    });

    onClose();
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    // Reset state
    setNewAccessibilityInformation(false);
    setLiveAccessibilityAlerts(false);
    // Call onComplete to let parent know we're completely done
    if (onComplete) {
      onComplete();
    }
  };

  const handleNewAccessibilityInformationToggle = (isOn: boolean) => {
    setNewAccessibilityInformation(isOn);

    // Track toggle change
    AnalyticsService.trackGAEventHandler("interestNotificationToggleChanged");
    AnalyticsService.trackMx("interestNotificationToggleChanged", {
      BusinessId: businessId,
      ToggleType: "newAccessibilityInformation",
      IsEnabled: isOn,
    });
  };

  const handleLiveAccessibilityAlertsToggle = (isOn: boolean) => {
    setLiveAccessibilityAlerts(isOn);

    // Track toggle change
    AnalyticsService.trackGAEventHandler("interestNotificationToggleChanged");
    AnalyticsService.trackMx("interestNotificationToggleChanged", {
      BusinessId: businessId,
      ToggleType: "liveAccessibilityAlerts",
      IsEnabled: isOn,
    });
  };

  const handleSubmit = () => {
    // Validate that an interest type is selected (mandatory)
    if (!selectedInterest) {
      showToast(i18n.get("pleaseSelectWhoYouAreGettingAlertsFor"), "error");
      return;
    }

    mutate({
      businessId: businessId,
      interestType: selectedInterest,
      notifyAccessibilityInformation: newAccessibilityInformation,
      notifyLiveAlert: liveAccessibilityAlerts,
      action: interestId ? "update" : "create",
      interestId: interestId,
    });
  };

  const isSubmitDisabled = isLoading || !selectedInterest;

  return (
    <>
      <Modal
        focusTrapped
        center
        blockScroll={true}
        animationDuration={0}
        open={isOpen}
        onClose={handleClose}
        classNames={{
          modal:
            "bg-[transparent] p-0 focus:outline-0 shadow-none w-[calc(100vw-32px)] md:w-[480px] max-w-[480px]",
          modalContainer: "!px-4",
          closeButton: "hidden",
        }}
      >
        <section
          className="bg-bg-brand-secondary-default rounded-2xl overflow-hidden w-full md:w-[480px] max-h-[90vh] flex flex-col"
          aria-labelledby="modal-title"
        >
          {isLoading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 rounded-2xl">
              <Loader />
            </div>
          )}
          
          {/* Screen reader announcement for loading state */}
          <div role="status" aria-live="polite" className="sr-only">
            {isLoading && "Saving notification preferences"}
          </div>

          {/* Header */}
          <header className="py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default shrink-0">
            <button
              ref={closeButtonRef}
              type="button"
              onClick={handleClose}
              className="flex-shrink-0 p-2 rounded-md hover:bg-bg-brand-secondary-hover transition-colors focus-blue"
              aria-label="Close modal"
            >
              <X
                size={20}
                className="text-fg-neutral-strong"
                aria-hidden="true"
              />
            </button>
            <h1
              id="modal-title"
              className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4"
            >
              {i18n.get("notifyme")}
            </h1>
            <div className="flex-shrink-0 w-10" aria-hidden="true" />
          </header>

          {/* Content */}
          <div className="flex-1 overflow-y-auto py-2 px-6">

            <span className="text-fg-brand-subtle label-m-medium font-bold font-['Inter'] leading-5">
              {businessName}
            </span>

            {/* Interest Type Selector */}
            <div className="mb-6">
              <p className="block label-m-medium text-fg-neutral-strong mb-3">
                {i18n.get("iMInterestedInAccessibilityInformationForThisPlaceFor")}
              </p>

              <div className="flex gap-2" role="radiogroup" aria-label={i18n.get("gettingAlertsFor")} aria-required="true">
                {interestTypes.map((interest, index) => (
                  <button
                    key={interest.value}
                    type="button"
                    role="radio"
                    aria-checked={selectedInterest === interest.value}
                    onClick={() => {
                      AnalyticsService.trackGAEventHandler("interestTypeSelected");
                      AnalyticsService.trackMx("interestTypeSelected", {
                        BusinessId: businessId,
                        InterestType: interest.value,
                      });
                      setSelectedInterest(interest.value as "for_myself" | "for_someone_i_know");
                    }}
                    className={`flex-1 flex flex-col items-center justify-center rounded-lg p-3 border-2 transition-colors ${
                      selectedInterest === interest.value
                        ? "border-border-token-brand-subtle bg-bg-brand-tertiary-default"
                        : "border-border-token-brand-minimal bg-bg-brand-ghost-default hover:bg-bg-brand-ghost-hover"
                    }`}
                  >
                    {index === 0 ? (
                      <User
                        size={24}
                        weight="regular"
                        className={
                          selectedInterest === interest.value
                            ? "text-fg-brand-subtle"
                            : "text-fg-neutral-strong"
                        }
                      />
                    ) : (
                      <UsersThree
                        size={24}
                        weight="regular"
                        className={
                          selectedInterest === interest.value
                            ? "text-fg-brand-subtle"
                            : "text-fg-neutral-strong"
                        }
                      />
                    )}
                    <span
                      className={`label-m-semibold mt-1 ${
                        selectedInterest === interest.value
                          ? "text-fg-brand-subtle"
                          : "text-fg-neutral-strong"
                      }`}
                    >
                      {interest.name === "For myself"
                          ? "Myself"
                          : interest.name === "For someone I know"
                            ? "Someone I know"
                            : interest.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Notification Toggles */}
            <div className="mb-6">
              <div className="self-stretch justify-center text-fg-neutral-strong mb-6">
                <span className="text-fg-neutral-strong label-m-medium font-medium font-['Inter'] leading-5">
                  {i18n.get("notifyMeAbout")}:
                </span>               
              </div>

              {/* New Accessibility Information Toggle */}
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-border-token-brand-minimal">
                <div className="flex items-center gap-2 flex-1">
                  <Bell
                    size={24}
                    className="text-fg-neutral-strong shrink-0"
                    aria-hidden="true"
                  />
                  <span id="switch-label-accessibility-info" className="label-m-medium text-fg-neutral-strong">
                    {i18n.get("newAccessibilityInformation")}
                  </span>
                </div>
                <Switch
                  isOn={newAccessibilityInformation}
                  onChange={handleNewAccessibilityInformationToggle}
                  aria-labelledby="switch-label-accessibility-info"
                />
              </div>

              {/* Live Accessibility Alerts Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1">
                  <Bell
                    size={24}
                    className="text-fg-neutral-strong shrink-0"
                    aria-hidden="true"
                  />
                  <span id="switch-label-live-alerts" className="label-m-medium text-fg-neutral-strong">
                    {i18n.get("liveAccessibilityAlerts")}
                  </span>
                </div>
                <Switch
                  isOn={liveAccessibilityAlerts}
                  onChange={handleLiveAccessibilityAlertsToggle}
                  aria-labelledby="switch-label-live-alerts"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <footer className="py-4 px-6 border-t border-border-token-brand-minimal bg-bg-brand-secondary-default shrink-0">
            <WideButton
              disabled={isSubmitDisabled}
              onClick={handleSubmit}
              aria-label={i18n.get("save")}
            >
              {i18n.get("done")}
            </WideButton>
          </footer>
        </section>
      </Modal>

      {/* Success Confirmation Modal */}
      <NotificationSuccessModal
        businessName={businessName}
        notifyAccessibilityInformation={newAccessibilityInformation}
        notifyLiveAlert={liveAccessibilityAlerts}
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
      />
    </>
  );
}

export default InterestNotificationModal;
